/**
 * Money + time formatting per spec §9: IDR with thousand-separator, no decimals.
 * Timestamps display in GMT+7.
 */
const idrFormatter = new Intl.NumberFormat('id-ID', {
  style: 'decimal',
  maximumFractionDigits: 0,
});

export function formatIdr(amount: number | bigint | string): string {
  const n = typeof amount === 'string' ? Number.parseInt(amount, 10) : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `Rp ${idrFormatter.format(n)}`;
}

/**
 * Render a UTC ISO timestamp into "YYYY-MM-DD HH:mm:ss" in GMT+7.
 */
export function formatGmt7(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const gmt7 = new Date(d.getTime() + 7 * 3600 * 1000);
  const y = gmt7.getUTCFullYear();
  const m = String(gmt7.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(gmt7.getUTCDate()).padStart(2, '0');
  const hh = String(gmt7.getUTCHours()).padStart(2, '0');
  const mm = String(gmt7.getUTCMinutes()).padStart(2, '0');
  const ss = String(gmt7.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${dd} ${hh}:${mm}:${ss}`;
}

export function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)} %`;
}

export function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Validate a plain IPv4 address (4 octets 0–255, no CIDR suffix, no leading
 * zeros). The IP whitelist now uses pure IPv4 — no `/32` etc.
 */
export function isIpv4(value: string): boolean {
  const v = value.trim();
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(v);
  if (!m) return false;
  return m.slice(1, 5).every((octet) => {
    const n = Number(octet);
    return n >= 0 && n <= 255 && String(n) === octet; // reject 256+ and leading zeros
  });
}

/**
 * Mantine color for a transaction status badge. Used by deposit and
 * disbursement list/detail pages so the same status shows the same color
 * everywhere.
 */
export function statusColor(s: 'pending' | 'paid' | 'failed'): string {
  if (s === 'paid') return 'green';
  if (s === 'failed') return 'red';
  return 'yellow';
}
